import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RotateCcw, Check } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API } from "../../lib/operatorWalk";
import { useContent } from "../../lib/content";
import { CONTENT_DEFAULTS, CONTENT_PAGES } from "../../content/defaults";
import AdminLayout from "../../components/admin/AdminLayout";

function FieldRow({ fieldKey, def, override, onSave, onReset, busy }) {
  const effective = override ?? def.text;
  const [value, setValue] = useState(effective);
  const dirty = value !== effective;
  const edited = override != null;

  useEffect(() => {
    setValue(override ?? def.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override]);

  const inputCls =
    "w-full border border-brand-200 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-500 bg-white";

  return (
    <div className="py-4 border-b border-brand-100 last:border-b-0" data-testid={`content-field-${fieldKey}`}>
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-xs uppercase tracking-[0.15em] text-ink-muted">{def.label}</span>
        {edited && (
          <span
            className="text-[10px] uppercase tracking-[0.12em] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5"
            data-testid={`content-edited-${fieldKey}`}
          >
            Edited
          </span>
        )}
      </div>
      {def.multiline ? (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={Math.min(6, Math.max(2, Math.ceil(value.length / 90)))}
          className={inputCls}
          data-testid={`content-input-${fieldKey}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={inputCls}
          data-testid={`content-input-${fieldKey}`}
        />
      )}
      <div className="mt-2 flex items-center gap-3">
        {dirty && (
          <button
            onClick={() => onSave(fieldKey, value)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-4 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
            data-testid={`content-save-${fieldKey}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save
          </button>
        )}
        {edited && (
          <button
            onClick={() => onReset(fieldKey)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 border border-brand-300 text-brand-700 hover:bg-brand-100 px-4 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
            data-testid={`content-reset-${fieldKey}`}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset to original
          </button>
        )}
        {edited && !dirty && (
          <span className="text-xs text-ink-muted truncate">Original: {def.text.slice(0, 80)}{def.text.length > 80 ? "..." : ""}</span>
        )}
      </div>
    </div>
  );
}

export default function AdminContent() {
  const { authHeader } = usePortalAuth();
  const { refresh: refreshPublic } = useContent();
  const [page, setPage] = useState("home");
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/content`, { headers: authHeader });
      const map = {};
      (res.data.overrides || []).forEach((r) => (map[r.key] = r.value));
      setOverrides(map);
    } catch {
      toast.error("Could not load site content.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => {
    const bySection = new Map();
    Object.entries(CONTENT_DEFAULTS)
      .filter(([, d]) => d.page === page)
      .forEach(([key, d]) => {
        if (!bySection.has(d.section)) bySection.set(d.section, []);
        bySection.get(d.section).push([key, d]);
      });
    return [...bySection.entries()];
  }, [page]);

  const editedCount = (p) =>
    Object.keys(overrides).filter((k) => CONTENT_DEFAULTS[k]?.page === p).length;

  const save = async (key, value) => {
    setBusyKey(key);
    try {
      const res = await axios.put(`${OW_API}/admin/content/${key}`, { value }, { headers: authHeader });
      if (res.data.reset) {
        setOverrides((o) => {
          const n = { ...o };
          delete n[key];
          return n;
        });
        toast.success("Field reset to the original text.");
      } else {
        setOverrides((o) => ({ ...o, [key]: value }));
        toast.success("Saved. Live on the site now.");
      }
      refreshPublic();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save.");
    } finally {
      setBusyKey("");
    }
  };

  const reset = async (key) => {
    setBusyKey(key);
    try {
      await axios.delete(`${OW_API}/admin/content/${key}`, { headers: authHeader });
      setOverrides((o) => {
        const n = { ...o };
        delete n[key];
        return n;
      });
      toast.success("Reset to the original text.");
      refreshPublic();
    } catch {
      toast.error("Could not reset.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <AdminLayout title="Site Content" subtitle="Edit the public site's copy. Changes go live immediately. Blank a field or hit Reset to return to the original text.">
    <div data-testid="admin-content-page">
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="content-page-tabs">
        {CONTENT_PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setPage(p.key)}
            className={`px-4 py-2 text-sm font-medium border transition-colors ${
              page === p.key
                ? "bg-brand-700 text-white border-brand-700"
                : "bg-white text-brand-700 border-brand-200 hover:border-brand-400"
            }`}
            data-testid={`content-tab-${p.key}`}
          >
            {p.label}
            {editedCount(p.key) > 0 && (
              <span className={`ml-2 text-[10px] px-1.5 py-0.5 ${page === p.key ? "bg-white/20" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                {editedCount(p.key)}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-ink-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading content
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map(([section, fields]) => (
            <div key={section} className="bg-white border border-brand-200" data-testid={`content-section-${section.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="bg-brand-100 px-5 py-3 font-serif text-xl text-brand-700">{section}</div>
              <div className="px-5">
                {fields.map(([key, def]) => (
                  <FieldRow
                    key={key}
                    fieldKey={key}
                    def={def}
                    override={overrides[key] ?? null}
                    onSave={save}
                    onReset={reset}
                    busy={busyKey === key}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
